var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import { cx, css as emotionCss } from 'emotion';
import { Close, Disabled, Gray, GrayClickable, IconContainer, Info, InfoClickable, Negative, NegativeClickable, Pending, PendingClickable, Pill, PillBorder, Positive, PositiveClickable, Tag as Default, TagClickable as DefaultClickable, Warning, WarningClickable, White, WhiteClickable, } from './Tag.styled';
import { colorIndexes } from '../styles/legacy';
import { clickable as clickableCitrus2022, colorIndexes as colorIndexesCitrus2022 } from '../styles/citron';
import { FuzzyHighlighter, Highlighter } from '@citrus/highlighter';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug } from '@citrus/runtime';
import { isNullOrEmpty, isNotNilOrEmpty } from '@pcty/utils';
import { getChildByType } from 'react-nanny';
import { useTheme } from '@citrus/user';
var VERSION = '4.3.1';
var Tag = React.forwardRef(function (_a, ref) {
    var _b;
    var automationId = _a.automationId, className = _a.className, _c = _a.colorIndex, colorIndex = _c === void 0 ? -1 : _c, css = _a.css, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.highlighter, highlighter = _e === void 0 ? 'fuzzy' : _e, onClick = _a.onClick, onClickClose = _a.onClickClose, renderIcon = _a.renderIcon, type = _a.type, externalStyleNames = _a.styleNames, value = _a.value, _f = _a.__TYPE, TYPE = _f === void 0 ? 'Tag' : _f, other = __rest(_a, ["automationId", "className", "colorIndex", "css", "disabled", "highlighter", "onClick", "onClickClose", "renderIcon", "type", "styleNames", "value", "__TYPE"]);
    var debug = useCitrusDebug();
    var citrus2022 = useTheme().citrus2022;
    var isCitron = citrus2022 !== null && citrus2022 !== void 0 ? citrus2022 : false;
    var colorIndexCss = getBackgroundColor(colorIndex, isCitron ? colorIndexesCitrus2022 : colorIndexes);
    var emotionObj = (_b = {},
        _b[emotionCss(colorIndexCss)] = isNotNilOrEmpty(colorIndexCss),
        _b[emotionCss(clickableCitrus2022)] = isNotNilOrEmpty(onClick) && isCitron,
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        colorIndexCss: emotionObj[emotionCss(colorIndexCss)],
        clickable: emotionObj[emotionCss(clickableCitrus2022)],
    };
    var Component = getComponent(type, colorIndexCss, disabled, isNotNilOrEmpty(onClick));
    return (React.createElement(Component, __assign({ className: classes, onClick: !disabled ? onClick : undefined, ref: ref, styleNames: __spreadArray(__spreadArray([], getStyleNames(styleNames), true), (externalStyleNames !== null && externalStyleNames !== void 0 ? externalStyleNames : []), true) }, other, getAutomation(automationId), getDebug(debug, TYPE, VERSION)),
        renderIcon && _renderIcon(renderIcon),
        renderValue(highlighter, value !== null && value !== void 0 ? value : ''),
        !disabled && renderClose(type, colorIndexCss, onClickClose, handleOnClickClose, value)));
});
var getComponent = function (type, colorIndexCss, disabled, clickable) {
    if (disabled === void 0) { disabled = false; }
    if (clickable === void 0) { clickable = false; }
    if (disabled)
        return Disabled;
    if (isNotNilOrEmpty(colorIndexCss))
        return clickable ? DefaultClickable : Default;
    switch (type) {
        case 'gray':
            return clickable ? GrayClickable : Gray;
        case 'info':
            return clickable ? InfoClickable : Info;
        case 'negative':
            return clickable ? NegativeClickable : Negative;
        case 'pending':
            return clickable ? PendingClickable : Pending;
        case 'pill':
            return Pill;
        case 'pill-border':
            return PillBorder;
        case 'positive':
            return clickable ? PositiveClickable : Positive;
        case 'warning':
            return clickable ? WarningClickable : Warning;
        case 'white':
            return clickable ? WhiteClickable : White;
        default:
            return clickable ? DefaultClickable : Default;
    }
};
var _renderIcon = function (renderIcon) {
    if (!renderIcon)
        return;
    var icon = getChildByType(renderIcon(), ['Icon']);
    if (!icon)
        return;
    return (React.createElement(IconContainer, null, React.cloneElement(icon, { bold: true, size: 'xsmall' })));
};
var renderValue = function (highlighter, value) {
    return highlighter === 'fuzzy' ? React.createElement(FuzzyHighlighter, { text: value }) : React.createElement(Highlighter, null, value);
};
var renderClose = function (type, colorIndexCss, onClickClose, handleOnClickClose, value) {
    var _a;
    if (onClickClose && isNullOrEmpty(colorIndexCss) && ((_a = type === null || type === void 0 ? void 0 : type.indexOf('pill')) !== null && _a !== void 0 ? _a : -1) >= 0) {
        return React.createElement(Close, { "aria-label": "".concat(value, " Dismiss"), onClick: function (e) { return handleOnClickClose(e, onClickClose); }, type: "button" });
    }
};
var getBackgroundColor = function (colorIndex, colorIndexes) {
    return colorIndex >= 0 && colorIndex <= 12 ? colorIndexes[colorIndex] : null;
};
var handleOnClickClose = function (e, onClickClose) {
    e.stopPropagation();
    onClickClose();
};
Tag.defaultProps = {
    __TYPE: 'Tag',
};
export default Tag;
